import {
  Checkbox,
  ItemWithCheckboxContainer,
  SelectWrapper,
  pushToDataLayer,
  selectControlStyles,
} from '@arnold/common';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { components } from 'react-select';
import { SelectFilterSelected } from '../../components/SelectFilterHelpers';
import auth from '../../lib/auth';
import { Filter, FilterOption } from '../../lib/reportHelper';
import { FilterBox } from './StyledComponents';

interface IProps {
  filters: Filter[];
  className?: string;
  options: { value: string; count: number; label?: string }[];
  setFilters: (filters: Filter[]) => void;
  type: 'position' | 'recruiter' | 'manager';
  disabled?: boolean;
  filterWithCount?: boolean;
  organizationId: string;
}

const GlobalGenericFilter = ({
  options,
  className,
  filters,
  setFilters,
  type,
  filterWithCount,
  organizationId,
  disabled,
}: IProps) => {
  const { t } = useTranslation('Report');
  const scrollPositionRef = React.useRef<number>(0);

  const selectValue = (optionValue: string) => (e: React.MouseEvent<any, MouseEvent>) => {
    const optionData = options.find((o) => o.value === optionValue);
    const userId = auth.getUserId();
    const existingFilter = filters.find((f) => f.editable && f.operator === type);
    const hasExistingValues = !!existingFilter?.values?.filter(Boolean).length;
    pushToDataLayer({
      event: hasExistingValues ? 'filter_updated' : 'filter_applied',
      hit: {
        event_type: 'generic_event',
        action_name: 'filter',
        ...(userId && { user_id: userId }),
        organization_id: organizationId,
        filter_category: 'structure',
        filter_item: optionData?.label ?? optionValue,
        filter_value: optionValue,
        filter_position: 3,
        count_results: optionData?.count,
      },
      _clear: true,
    });

    const filtersCopy = [...filters];

    let filter = filtersCopy.find((filter) => filter.editable && filter.operator === type);
    if (!filter) {
      filter = {
        operator: type,
        editable: true,
        values: [],
      };
      filtersCopy.push(filter);
    }
    if (filter.values == null) filter.values = [];

    if (filter.values.includes(optionValue)) {
      filter.values = filter.values.filter((value) => value !== optionValue);
    } else {
      filter.values.push(optionValue);
    }
    setFilters(filtersCopy);

    e.preventDefault();
  };

  const selectedValues =
    filters
      .find((filter) => filter.operator === type)
      ?.values?.filter(Boolean)
      .map((value) => value!.toString()) ?? [];

  const optionsByValue = new Map(options.map((option) => [option.value.toString(), option]));
  const validSelectedValues = selectedValues.filter((selectedValue) => optionsByValue.has(selectedValue));

  const selectedOptionsTextForm = validSelectedValues
    .map((selectedValue) => {
      const option = optionsByValue.get(selectedValue);
      return option?.label ?? option?.value;
    })
    .filter(Boolean) as string[];

  const filteredOptions = options.filter((option) =>
    filterWithCount ? option.count || validSelectedValues.includes(option.value.toString()) : true,
  );

  const CustomMenu = (props: { opt: FilterOption }) => {
    const menuRef = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
      const handle = () => {
        if (menuRef.current) {
          scrollPositionRef.current = menuRef.current.scrollTop;
        }
      };
      if (menuRef.current) {
        menuRef.current.scrollTop = scrollPositionRef.current;
        menuRef.current.addEventListener('scroll', handle);
      }
      return () => {
        if (menuRef.current) {
          // eslint-disable-next-line react-hooks/exhaustive-deps
          menuRef.current.removeEventListener('scroll', handle);
        }
      };
    }, []);

    return (
      <components.Menu {...props}>
        <FilterBox
          ref={menuRef}
          className={'bg-white rounded-lg shadow-sm position-absolute'}
          data-cy={`report-${type}-filter-menu`}
        >
          {filteredOptions.map((option) => (
            <ItemWithCheckboxContainer key={option.value}>
              <Checkbox
                selected={validSelectedValues.includes(option.value.toString())}
                onToggle={selectValue(option.value)}
                text={
                  <>
                    <span>{option.label ?? option.value}</span>
                    <span className={'text-muted'}>&nbsp;({option.count})</span>
                  </>
                }
                labelStyles="margin: 8px 0;display: flex;align-items: center;justify-content: space-between;"
              />
            </ItemWithCheckboxContainer>
          ))}
        </FilterBox>
      </components.Menu>
    );
  };

  const CustomPlaceholder = () => {
    return (
      <SelectFilterSelected
        emptyPlaceholder={t(`filterBy${type}`)}
        selectedAnswers={selectedOptionsTextForm}
        disabled={filteredOptions.length === 0}
      />
    );
  };

  return (
    <SelectWrapper
      blurInputOnSelect={false}
      menuShouldScrollIntoView={false}
      disabled={disabled}
      className={`react-select ${className || ''}`}
      components={{
        IndicatorSeparator: () => null,
        Menu: CustomMenu,
        Placeholder: CustomPlaceholder,
      }}
      styles={{
        container: (provided: any, state: any) => ({
          ...provided,
          width: '100% !important',
          margin: '0px',
        }),
        control: (provided: any, state: any) => ({
          ...selectControlStyles(provided, state),
          paddingLeft: '0',
        }),
        valueContainer: (provided: any, state: any) => ({
          ...provided,
          display: 'flex',
        }),
      }}
      isDisabled={filteredOptions.length === 0}
      isSearchable={false}
      isMulti={false}
      closeMenuOnSelect={false}
    />
  );
};

export default GlobalGenericFilter;
